import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { convertSecondsToMinutes, sortBookPages } from '../services/builderHelpers';
import FrontCoverPages from './FrontCoverPages/FrontCoverPages';
import EditImageTextIllustration from './EditImageTextIllustration';
import ChooseImageFromHistory from './ImageHistory';
import { EditStorySummary } from './EditStorySummary';
import { isFirebaseDateInFuture, scrollToArea } from '../../../helper/helper';
import Modal from '../../../components/Common/modal/Modal';
import { CONFIG } from '../../../config/mainConfig';
import { Toast } from '../../../components/Common/Toast';
import Button from '../../../components/Common/Button';
import Loader from '../../../components/Common/Loader';
import { PageDetailsModel } from '../../../interfaces/common.interface';
import { IStoryData, setStoryData } from '../../../store/storySlice';
import { openPlanPurchaseModal, setBookData, setBookDataModified } from '../../../store/appSlice';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import BackCoverPages from './BackCoverPages/BackCoverPages';
import { switchPageImage } from '../services/story.service';
import { StoryPagesProps } from '../models/StoryPagesProps.interface';
import { MidjourneyModeGeneration, StoryAgeGroupTypeEnum } from '../../../interfaces/book.interface';
import { UserLocalStorageKeysEnum } from '../../../interfaces/user.interface';
import UnlockImage from '../../../components/UnlockImage/UnlockImage';
import { ListViewMode } from './ListViewModeSwitcher';
import { SecureImg } from '../../../components/Common/SecureImage';
import { StoryPagesImage } from './StoryPagesImage';

const StoryPages = ({
  bookData,
  openModal,
  edit = false,
  loaderImageGeneration = null,
  setShowImageHistory = null,
  mode,
  setShowLowCreditModal,
  setShowBookCover,
  showBookCover,
  selectedCoverUrl,
  displayBookPages,
  setStateData,
  stateData,
  summaryModal,
  setSummaryModal,
  setShowStoryForm,
  setimageCharacterPrompt,
  imageCharacterPrompt,
  showEditTextIllustration,
  setShowEditTextIllustration,
  editing,
  setEditing,
  setShowCharacterLibrary,
}: StoryPagesProps) => {
  const navigate = useNavigate();
  const storyData: IStoryData = useSelector((state: RootState) => state.story.storyData);
  const user = useSelector((state: RootState) => state.user.user);
  const targetRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [showPlanModal, setShowPlanModal] = useState<boolean>(false);
  const [showRelaxModeModal, setShowRelaxModeModal] = useState<boolean>(false);
  const [pages, setPages] = useState([]);
  const [isUpdateImage, setIsUpdateImage] = useState<{ [key: number]: boolean }>({});
  const [deadlineStatus, setDeadlineStatus] = useState<{ [key: string]: 'init' | 'pending' | 'done' }>({});
  const [updateImgIndex, setUpdateImgIndex] = useState(null);
  const processedKeys = useRef(new Set());
  const dispatch: AppDispatch = useDispatch();

  // useEffect(() => {
  //   if (pages && pages.length > 0) {
  //     const newDeadlineStatuses: { [key: string]: 'init' | 'pending' | 'done' } = {};
  //
  //     pages.forEach(([pageIndex, page]: any) => {
  //       const isDeadlineIssue =
  //         page.job_id &&
  //         !page.imageURL &&
  //         !!page.deadlineImageGenerationAt &&
  //         !isFirebaseDateInFuture(page.deadlineImageGenerationAt);
  //
  //       if (isDeadlineIssue && !deadlineStatus[pageIndex] && !processedKeys.current.has(pageIndex)) {
  //         newDeadlineStatuses[pageIndex] = 'init';
  //       }
  //     });
  //
  //     if (Object.keys(newDeadlineStatuses).length > 0) {
  //       setDeadlineStatus(prev => ({ ...prev, ...newDeadlineStatuses }));
  //     }
  //   }
  // }, [pages]);
  //
  // useEffect(() => {
  //   if (Object.values(deadlineStatus).some((item) => item === 'init')) {
  //     const pagesStatus = Object.keys(deadlineStatus)
  //       .map((key) => ({ key, status: deadlineStatus[key] }))
  //       .filter((item) => item.status === 'init' && !processedKeys.current.has(item.key));
  //
  //     pagesStatus.forEach((item) => {
  //       processedKeys.current.add(item.key);
  //       setDeadlineStatus((prevState) => ({
  //         ...prevState,
  //         [item.key]: 'pending',
  //       }));
  //       stopBookPageGeneration(bookData?.bookId, item.key)
  //         .then(() =>
  //           setDeadlineStatus((prevState) => ({
  //             ...prevState,
  //             [item.key]: 'done',
  //           })),
  //         )
  //         .catch();
  //     });
  //   }
  // }, [deadlineStatus, bookData?.bookId]);

  const summaryId = 0;
  const closeSummaryModal = () => {
    setSummaryModal(false);
    scrollToArea(targetRefs, 'edit-summary');
  };

  const [mainImages, setMainImages] = useState(Array(bookData.pages.length).fill(''));

  // Function to handle image click for a specific card
  const handleImageClick = async (imageURL: string, imageURLWebp: string, index: number, pageIndex: any) => {
    setUpdateImgIndex(pageIndex);
    setIsUpdateImage({ ...isUpdateImage, [pageIndex]: true });

    const updatedMainImages = [...mainImages];
    updatedMainImages[index] = `url(${imageURL})`;
    setMainImages(updatedMainImages);

    // Optimistic local clear — picking a known-good prior image is the
    // user's chosen resolution to whatever went wrong on the latest
    // attempt, so the error UI must disappear immediately. The server
    // (Book.service.ts switchPagePhoto) does the same clear; we do it
    // here too so the UI flips on click without waiting for the
    // Firestore listener round-trip.
    if (bookData?.pages?.[pageIndex]) {
      const currentPage = bookData.pages[pageIndex];
      const isDirty =
        !!currentPage.error || !!currentPage.errorCategory || !!currentPage.contentFilter;
      if (isDirty) {
        dispatch(
          setBookData({
            ...bookData,
            pages: {
              ...bookData.pages,
              [pageIndex]: {
                ...currentPage,
                imageURL,
                imageURLwebp: imageURLWebp,
                error: null,
                errorCategory: null,
                contentFilter: null,
              },
            },
          }),
        );
      }
    }

    try {
      const response = await switchPageImage(bookData?.bookId, index, imageURL, imageURLWebp);

      if (response) {
        if (localStorage.getItem(UserLocalStorageKeysEnum.EDIT_MODE) === 'yes') {
          dispatch(setBookDataModified(true));
        }
      }

      setIsUpdateImage({ ...isUpdateImage, [pageIndex]: false });
      setUpdateImgIndex(null);
    } catch (error) {
      Toast('Image Switch Failed', true);
      setIsUpdateImage({ ...isUpdateImage, [pageIndex]: false });
      setUpdateImgIndex(null);
    }
  };

  useEffect(() => {
    sortBookPages(bookData, setPages, !!user?.isTrialFreeAccess);
  }, [bookData]);

  useEffect(() => {
    if (storyData?.illustrationCharacter?.index) {
      setEditing({
        ...editing,
        bookId: bookData?.bookId,
        pageIndex: storyData?.illustrationCharacter?.pageIndex,
        pageData:
          (storyData?.illustrationCharacter?.pageIndex || storyData?.illustrationCharacter?.pageIndex === 0) &&
          bookData?.pages[storyData?.illustrationCharacter?.pageIndex],
        type: 'editPrompt',
        index: storyData?.illustrationCharacter?.index,
      });
      setShowEditTextIllustration(true);
    }
  }, [bookData, storyData]);

  let totalPages = bookData?.pages;
  totalPages = Object.keys(totalPages).length;

  const showSubscriptionModal = () => {
    dispatch(
      openPlanPurchaseModal({
        show: true,
        isFinishStory: true,
        data: {
          title: 'Upgrade Plan!',
          description:
            "Order a physical copy of the book you're creating, or subscribe to a limited-time free trial to finish your current story and start new ones!",
        },
      }),
    );
  };

  // Credits gate used by the Edit-scene action. Generation itself is
  // triggered later from the editor (which deducts when the user saves);
  // we don't deduct here just for opening the editor.
  const hasEnoughCredits = () =>
    +(storyData?.creditsPerBookLimit ?? 0) >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE &&
    +bookData?.creditsPerBookLimit >= CONFIG.BILLING.DEDUCT_CREDITS_PER_IMAGE;

  // Single render path for the per-page action area. Replaces what used to
  // be two diverging components (RegenerateImage for failures, GeneralProcessButtons
  // for success). Layout is constant across states; only the message line and
  // which left-side button shows differ. See feedback memory
  // `feedback_image_error_ux.md` for the bucket rules.
  const ImageActions = (
    page: PageDetailsModel,
    pageIndex: string,
    index: number,
    showActionButtons: boolean = true,
  ) => {
    const isPageComplete = !!page?.imageURL;
    const isStillProcessing =
      (loaderImageGeneration && loaderImageGeneration[pageIndex]) ||
      (page?.progress !== null && page?.progress !== undefined && +page?.progress >= 0);

    // All branches share the SAME outer wrapper shape so the action area
    // anchors to the bottom of the column at the same Y across states
    // (queue / processing / success / failure). Only the inner content
    // differs.
    if (page?.queuePosition && page?.queueEstimatedTime && !isPageComplete) {
      return (
        <div className="mt-auto pt-4 pb-4 flex flex-col items-center w-full">
          <div className="w-full bg-storyblueSecondLightText flex gap-1 justify-between items-center text-white font-sans rounded-md p-2 text-xs px-2 h-8">
            <span>
              Queue position: <strong className="ml-2">{page.queuePosition}</strong>
            </span>
            <span>
              Estimate Time:
              <strong className="ml-2">{convertSecondsToMinutes(page?.queueEstimatedTime)}</strong>
            </span>
          </div>
        </div>
      );
    }

    if (isStillProcessing) {
      return (
        <div className="mt-auto pt-4 pb-4 flex flex-col items-center">
          <Button
            title={
              <>
                <span>Processing </span>
                <Loader loader={loaderImageGeneration[pageIndex] || +page?.progress! >= 0} />
                {+page?.progress! > 0 && `${page.progress}%`}
              </>
            }
            disabled
            className="bg-storyblueSecondLightText flex gap-1 items-center text-white font-sans rounded-2xl px-4 py-2 text-xs h-8"
            onClick={() => {}}
          />
        </div>
      );
    }

    if (!showActionButtons) {
      return null;
    }

    // Tile-level failure summary — kept to a single line with an error
    // treatment so the visual signal is clear at a glance. The detailed
    // copy (including support email for generic failures and the
    // category-specific guidance for moderation) lives in the editor
    // banner where the user can act on it.
    const isError = !!page?.error;
    const isModeration = !!page?.contentFilter?.flagged;
    const failureMessage = !isError
      ? null
      : isModeration
        ? 'This scene needs adjusting'
        : 'Image generation failed';

    const hasLibrary = (bookData?.imageLibrary?.length || 0) > 0;

    const handleEditScene = () => {
      if (!hasEnoughCredits()) {
        if (
          user?.plan?.name !== CONFIG.PROMO_PLAN &&
          bookData?.modeIllustrationGeneration !== MidjourneyModeGeneration.relaxed
        ) {
          setShowRelaxModeModal(true);
        } else {
          Toast('Additional Image Credits Needed', true);
        }
        return;
      }
      setEditing({
        ...editing,
        bookId: bookData?.bookId,
        pageIndex,
        pageData: page,
        type: 'editPrompt',
        index: index * 2 + 2,
      });
      setShowEditTextIllustration(true);
    };

    const openLibrary = () => {
      setStateData({ ...stateData, pageIndex, showImageLibrary: true });
      setShowImageHistory(pageIndex);
    };

    // Layout note: this action area uses the SAME wrapper shape as the
    // text-side "Edit Text" wrapper:
    //   <div className="mt-auto pt-4 pb-4 flex items-center justify-center">
    // mt-auto pushes us to the bottom of the parent column; pt-4/pb-4
    // give us the same 1rem top/bottom margin as the text side, so both
    // columns end at the same Y.
    //
    // The failure message is positioned ABSOLUTELY below the wrapper so
    // it doesn't contribute to the wrapper's box height. If it were a
    // normal flow child, adding it would make the wrapper taller and
    // push the buttons up, breaking horizontal alignment with the
    // text-side button on the same row. Absolute = the buttons stay put
    // at the same Y whether or not there's a message.
    return (
      <div className="relative mt-auto pt-4 pb-4 flex flex-col items-center">
        <div className="flex gap-2 items-center justify-center">
          <Button
            title="Edit scene"
            className="bg-storyblueSecondLightText flex gap-1 items-center text-white font-sans rounded-2xl px-4 py-2 text-xs h-8"
            onClick={handleEditScene}
          />
          {hasLibrary && (
            <Button
              title="Image Library"
              className="bg-storyblueSecondLightText flex gap-1 items-center text-white font-sans rounded-2xl px-4 py-2 text-xs h-8"
              onClick={openLibrary}
            />
          )}
        </div>
        {failureMessage && (
          <p className="absolute left-0 right-0 text-xs text-storyerror font-semibold text-center px-2 truncate" style={{ top: '100%' }}>
            {failureMessage}
          </p>
        )}
      </div>
    );
  };
  const showCloseButtonFunction = () => {
    setShowPlanModal(false);
  };

  const finishStory = () => {
    dispatch(
      openPlanPurchaseModal({
        show: true,
        data: {
          title: 'Finish your story!',
          description:
            'Unlock your last images and finish your personalized book by ordering a physical copy, or join a subscription plan to create more books for your library and print your favorites!',
        },
        isFinishStory: true,
      }),
    );
  };

  return (
    <>
      {summaryModal ? (
        <EditStorySummary
          summary={bookData?.summary}
          type={mode}
          closeSummaryModal={closeSummaryModal}
          displayBookPages={displayBookPages}
          targetRefs={targetRefs}
          summaryId={summaryId}
        />
      ) : (
        <>
          {showEditTextIllustration ? (
            <EditImageTextIllustration
              bookId={editing.bookId!}
              pageIndex={editing.pageIndex!}
              pageData={editing.pageData}
              type={editing.type!}
              index={editing.index!}
              setShowEditTextIllustration={setShowEditTextIllustration}
              setShowLowCreditModal={setShowLowCreditModal}
              displayBookPages={displayBookPages}
              setStateData={setStateData}
              stateData={stateData}
              targetRefs={targetRefs}
              setShowStoryForm={setShowStoryForm}
              setimageCharacterPrompt={setimageCharacterPrompt}
              editing={editing}
              setShowCharacterLibrary={setShowCharacterLibrary}
            />
          ) : stateData?.showImageLibrary ? (
            <ChooseImageFromHistory
              bookData={bookData}
              pageIndex={stateData?.pageIndex}
              setShowImageHistory={setShowImageHistory}
              displayBookPages={displayBookPages}
              setStateData={setStateData}
              stateData={stateData}
              targetRefs={targetRefs}
            />
          ) : (
            <>
              {showPlanModal && (
                <Modal
                  title="Plan Purchase"
                  description="Please choose a plan to continue working on your book!."
                  doProcess={() => navigate('/myPlans')}
                  showCloseButtonFunction={showCloseButtonFunction}
                  showCloseButton={true}
                />
              )}
              {showRelaxModeModal && bookData?.modeIllustrationGeneration !== MidjourneyModeGeneration.relaxed && (
                <Modal
                  description="Sorry, you have run out of image credits. Any pages with prior images can still be accessed from their Image Library."
                  doProcess={() => setShowRelaxModeModal(false)}
                  showCloseButton={false}
                  showSubmitButton={true}
                  submitButtonTitle={'Continue'}
                />
              )}
              <FrontCoverPages
                bookData={bookData}
                editCover={true}
                setShowBookCover={setShowBookCover}
                showBookCover={showBookCover}
                selectedCoverUrl={selectedCoverUrl}
                displayBookPages={displayBookPages}
                mode={mode}
                setStateData={setStateData}
                stateData={stateData}
              />
              <div className={` ${mode === ListViewMode.PAGE ? '  ' : 'grid grid-cols-1 lg:grid-cols-2 gap-4'} `}>
                {pages &&
                  pages?.length > 0 &&
                  pages?.map(([pageIndex, page]: any, index) => {
                    // const isDeadlineIssue =
                    //   page.job_id &&
                    //   !page.imageURL &&
                    //   !!page.deadlineImageGenerationAt &&
                    //   !isFirebaseDateInFuture(page.deadlineImageGenerationAt);
                    return (
                      <div
                        id={pageIndex}
                        key={pageIndex}
                        ref={(ref: any) => (targetRefs.current[pageIndex] = ref)}
                        className={` ${mode === ListViewMode.PAGE
                            ? ' border-none '
                            : 'm-2 md:flex md:justify-center gap-4 p-4 xs:flex-wrap '
                          }  rounded border-none
                        ${page.block ? 'block' : ''}`}
                      >
                        {/*
                          Both columns share the same vertical structure:
                            - top:    the panel (text or image)
                            - middle: optional secondary content (thumbnails)
                            - bottom: action area, anchored via mt-auto

                          mt-auto on the action area is what guarantees both
                          columns end at the same Y regardless of how many
                          siblings precede the action area. justify-between
                          can't do that — with 3+ children it distributes
                          space evenly and pulls the action area toward the
                          middle.
                        */}
                        <div className="flex flex-col items-center rounded-lg h-full">
                          <div
                            className={`  ${mode === ListViewMode.PAGE
                                ? ' h-[350px] w-[350px]  md:w-[600px] md:h-[600px] '
                                : ' w-72 h-72'
                              }  text-2xl  overflow-auto px-8 flex flex-col items-center justify-center text-center font-serif bg-white rounded-lg relative`}
                            style={{
                              backgroundImage: `url("/paper.jpg")`,
                              backgroundSize: 'cover',
                            }}
                          >
                            <h3
                              className={`font-bold text-left font-poppins ${bookData?.childAge === StoryAgeGroupTypeEnum.AGE_0_2
                                  ? 'text-2xl'
                                  : bookData?.childAge === StoryAgeGroupTypeEnum.AGE_3_7
                                    ? 'text-xl'
                                    : bookData?.childAge === StoryAgeGroupTypeEnum.AGE_8_12
                                      ? 'text-lg'
                                      : 'text-sm'
                                } ${mode === ListViewMode.GRID ? 'h-[13.5rem] overflow-y-auto flex items-start' : ''}`}
                            >
                              {page?.text}
                            </h3>
                            <div className="page-number mb-2 w-full flex justify-center absolute bottom-0 left-1/2 transform -translate-x-1/2">
                              <div
                                className=" text-base w-12 h-6 flex justify-center items-center font-bold text-storyblue"
                                style={{ fontFamily: 'system-ui' }}
                              >
                                {index * 2 + 5}
                              </div>
                            </div>
                          </div>
                          {edit && (
                            <div className="mt-auto pt-4 pb-4 flex items-center justify-center">
                              <Button
                                title="Edit Text"
                                onClick={() => {
                                  setEditing({
                                    ...editing,
                                    bookId: bookData?.bookId,
                                    pageIndex,
                                    pageData: page,
                                    type: 'editText',
                                    index: index * 2 + 1,
                                  });
                                  setShowEditTextIllustration(true);
                                }}
                                className="bg-storyblueSecondLightText flex text-white font-sans rounded-2xl px-4 py-2 text-xs justify-center"
                              />
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col items-center h-full">
                          <div className={`flex flex-col justify-center items-center`} style={{ position: 'relative' }}>
                            <StoryPagesImage
                              page={page}
                              pageIndex={pageIndex}
                              index={index}
                              user={user}
                              mode={mode}
                              imageURL={page.imageURLwebp || page.imageURL}
                              hasError={!!page?.contentFilter?.flagged || (!page?.imageURL && !page?.progress && !!page?.error)}
                            ></StoryPagesImage>
                            {page.block ? <UnlockImage submit={finishStory}></UnlockImage> : ''}
                          </div>
                          {page?.images && page?.images?.length > 1 && !page.block && (
                            <div className="mt-2 flex ">
                              {page?.images?.map((img: any, imgIndex: number) => {
                                const isActiveImage = img.imageURL === page.imageURL;
                                return (
                                  <div
                                    key={img.imageURL || imgIndex}
                                    style={{
                                      pointerEvents: isUpdateImage[pageIndex] ? 'none' : 'auto',
                                      opacity: isUpdateImage[pageIndex] ? 0.5 : 1,
                                    }}
                                    className={`flex flex-col  gap-1    ${mode === ListViewMode.GRID ? 'mt-2px' : 'mt-1'
                                      }`}
                                  >
                                    <SecureImg
                                      storageSrc={img.imageURL}
                                      alt="Image 1"
                                      style={{
                                        pointerEvents: isUpdateImage[pageIndex] ? 'none' : 'auto',
                                      }}
                                      className={`  ${isUpdateImage[pageIndex] ? 'opacity-50' : 'opacity-100'}  ${mode === ListViewMode.PAGE
                                          ? 'w-[80px] h-[80px] md:w-[145px] md:h-[145px]'
                                          : ' w-[60px] h-[60px]'
                                        }  rounded cursor-pointer border-rounded border-2 ${isActiveImage ? 'border-storyblue ring-2 ring-storyblue' : 'border-white'}`}
                                      onClick={() => {
                                        handleImageClick(img.imageURL, img.imageURLwebp || img.imageURL, index, pageIndex);
                                      }}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {page?.error
                            ? ImageActions(page, pageIndex, index, true)
                            : edit && !page.block && ImageActions(page, pageIndex, index, !page.block)}
                        </div>
                      </div>
                    );
                  })}
                <div className="m-0 md:m-2 h-full">
                  <BackCoverPages
                    bookData={bookData}
                    mode={mode}
                    displayBookPages={displayBookPages}
                    setSummaryModal={setSummaryModal}
                    targetRefs={targetRefs}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </>
  );
};

export default StoryPages;
